import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { NgbProgressbar, NgbProgressbarModule } from '@ng-bootstrap/ng-bootstrap';
import { ProjectsService } from 'src/app/services/api/projects.service';
import { HttpParams } from '@angular/common/http';
import { InputFieldComponent } from 'src/app/components/form/input-field/input-field.component';
import { SelectFieldComponent } from 'src/app/components/form/select-field/select-field.component';
import { TextAreaFieldComponent } from 'src/app/components/form/textarea-field/textarea-field.component';
import { ConfirmationDialogService } from 'src/app/services/confirmation-dialog.service';
import { FormNotesCustomersComponent } from "src/app/modules/customers/components/notes-customers-form/notes-customers-form.component";
import { SubmittableComponent } from '../projects-accordion/projects-accordion.component';
import { AlertNotificationService } from 'src/app/services/alert-notification.service';
import { AccessControlService } from 'src/app/services/access-control.service';
import { MainLayoutService } from 'src/app/services/main-layout.service';
import { ProjectsHistoryLogsComponent } from '../projects-history-logs/projects-history-logs.component';

@Component({
  selector: 'projects-summary',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    InputFieldComponent,
    SelectFieldComponent,
    TextAreaFieldComponent,
    NgbProgressbarModule,
    FormNotesCustomersComponent,
    ProjectsHistoryLogsComponent
  ],
  templateUrl: './projects-summary.component.html',
  styleUrl: './projects-summary.component.scss'
})
export class ProjectsSummaryComponent implements OnInit, OnChanges, SubmittableComponent {
  @Input() projectId?: string;
  @Input() project?: any;
  @Output() onSummaryUpdated = new EventEmitter<any>();

  summaryForm!: FormGroup;

  projectStartDateControl: FormControl = new FormControl('');
  projectStatusControl: FormControl = new FormControl('');
  proposalSummaryControl: FormControl = new FormControl('');
  proposalDescriptionControl: FormControl = new FormControl('');
  preliminaryInfoControl: FormControl = new FormControl('');
  progressNoteControl: FormControl = new FormControl('');
  inspectorSelectControl: FormControl = new FormControl('');
  reviewedProjectControl: FormControl = new FormControl(false);

  statusOptions = [
    { key: 'TO_ASSIGN', value: 'To Assign' },
    { key: 'WORKING', value: 'Working' },
    { key: 'STANDBY', value: 'Standby' },
    { key: 'CLOSED', value: 'Closed' }
  ];

  inspectorOptions: any[] = [];
  availableInspectors: any[] = [];
  inspectors: any[] = [];
  isLoadingInspectors = false;
  isLoadingAvailableInspectors = false;

  private _statusProgress: number = 0;
  progressValue: number = 0;
  progressDone: number = 0;
  progressToDo: number = 0;
  activeView: 'done' | 'todo' = 'done';

  inspectorToDelete: any = null;

  projectNotes: any[] = [];
  canViewReviewedCheckbox = false;
  canModifyReviewedCheckbox = false;
  canViewInspectors = false;
  canModifyInspectors = false;
  canViewNotes = false;
  canViewProgressNote = false;
  private activeProfile: string | null = null;

  constructor(
    private fb: FormBuilder,
    protected projectsService: ProjectsService,
    private alertNotificationService: AlertNotificationService,
    private confirmationDialogService: ConfirmationDialogService,
    private accessControlService: AccessControlService,
    private mainLayoutService: MainLayoutService
  ) {
    this.summaryForm = this.fb.group({
      projectStartDate: this.projectStartDateControl,
      projectStatus: this.projectStatusControl,
      proposalSummary: this.proposalSummaryControl,
      proposalDescription: this.proposalDescriptionControl,
      preliminaryInfo: this.preliminaryInfoControl,
      progressNote: this.progressNoteControl,
      inspector: this.inspectorSelectControl,
      reviewedProject: this.reviewedProjectControl
    });
  }

  ngOnInit(): void {
    this.mainLayoutService.activeProfile$.subscribe(profileCode => {
      this.activeProfile = profileCode;
      this.canViewReviewedCheckbox = this.accessControlService.hasFeatureActionAccess(
        profileCode, 'PROJECTS_SUMMARY_REVIEWED_CHECKBOX', 'view'
      );
      this.canModifyReviewedCheckbox = this.accessControlService.hasFeatureActionAccess(
        profileCode, 'PROJECTS_SUMMARY_REVIEWED_CHECKBOX', 'modify'
      );
      this.canViewInspectors = this.accessControlService.hasFeatureActionAccess(
        profileCode, 'PROJECTS_SUMMARY_INSPECTORS', 'view'
      );
      this.canModifyInspectors = this.accessControlService.hasFeatureActionAccess(
        profileCode, 'PROJECTS_SUMMARY_INSPECTORS', 'modify'
      );
      this.canViewNotes = this.accessControlService.hasFeatureActionAccess(
        profileCode, 'PROJECTS_SUMMARY_NOTES', 'view'
      );
      this.canViewProgressNote = this.accessControlService.hasFeatureActionAccess(
        profileCode, 'PROJECTS_SUMMARY_PROGRESS_NOTE', 'view'
      );

      if (this.accessControlService.hasFeatureActionAccess(profileCode, 'PROJECTS_SUMMARY_FIELDS_EDIT', 'modify')) {
        this.summaryForm.enable({ emitEvent: false });
      } else {
        this.summaryForm.disable({ emitEvent: false });
      }

      this.updateReviewedCheckboxState();
    });

    if (this.projectId) {
      this.loadProjectSummary();
      this.loadProjectInspectors();
      this.loadAvailableInspectors();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['project'] && this.project) {
      const projectData = this.project;      
      this.projectStartDateControl.setValue(projectData.startTime || '');
      this.projectStatusControl.setValue(projectData.status || '');
      this.proposalSummaryControl.setValue(projectData.proposalSummary || '');
      this.proposalDescriptionControl.setValue(projectData.proposalDescription || '');
      this.preliminaryInfoControl.setValue(projectData.customerPreliminaryInfo || '');
      this.progressNoteControl.setValue(projectData.progressNote || '');
      this.reviewedProjectControl.setValue(projectData.reviewed || false);
      
      if (projectData.statusProgress !== undefined) {
        this.updateProgressValues(projectData.statusProgress);
      }
      
      
      if (projectData.inspectors && Array.isArray(projectData.inspectors)) {
        this.inspectors = projectData.inspectors;
      }

      this.updateReviewedCheckboxState();
    }

    if (changes['projectId'] && this.projectId) {
      this.loadProjectInspectors();
      this.loadAvailableInspectors();
    }
  }



  loadProjectSummary(): void {
    if (this.projectId) {
      const params = new HttpParams().set('size', '9999');
      this.projectsService.listSummaryNotes(Number(this.projectId), { params }).subscribe({
        next: (response: any) => {
          if (response.status && response.status.code === 200) {
            this.projectNotes = response?.data?.sectionNotes || [];
          }
        },
        error: (error: any) => {
          console.error('Error loading project notes:', error);
        }
      });
    }
  }

  canToggleReviewedProject(): boolean {
    if (!this.canModifyReviewedCheckbox) {
      return false;
    }

    if (this.activeProfile === 'CTO1') {
      return true;
    }

    const testlistCount = this.project?.testlistCount ?? 0;
    const testlistPassedCount = this.project?.testlistPassedCount ?? 0;
    return testlistCount > 0 && testlistPassedCount === testlistCount;
  }

  private updateReviewedCheckboxState(): void {
    if (this.canToggleReviewedProject()) {
      this.reviewedProjectControl.enable({ emitEvent: false });
    } else {
      this.reviewedProjectControl.disable({ emitEvent: false });
    }
  }

  private updateProgressValues(progress: number): void {
    this._statusProgress = Math.min(100, Math.max(0, progress)); 
    this.progressDone = this._statusProgress;
    this.progressToDo = 100 - this._statusProgress;
    this.progressValue = this.activeView === 'done' ? this.progressDone : this.progressToDo;
  }

  toggleProgress(view: 'done' | 'todo'): void {
    this.activeView = view;
    this.progressValue = view === 'done' ? this.progressDone : this.progressToDo;
  }

  onSubmit(): void {
    if (this.summaryForm.valid) {
      const formData = this.summaryForm.getRawValue();
      this.onSummaryUpdated.emit(formData);
    }
  }

  submitForm(): void {
    if (!this.summaryForm.valid) {
      this.alertNotificationService.error('Please fill in all required fields correctly');
      return;
    }

    if (!this.projectId) {
      this.alertNotificationService.error('Invalid project ID');
      return;
    }

    const formData = this.summaryForm.getRawValue();
    const projectData = {
      startTime: formData.projectStartDate,
      status: formData.projectStatus,
      proposalSummary: formData.proposalSummary,
      proposalDescription: formData.proposalDescription,
      customerPreliminaryInfo: formData.preliminaryInfo,
      statusProgress: this.progressDone,
      reviewed: formData.reviewedProject,
      ...(this.canViewProgressNote ? { progressNote: formData.progressNote } : {})
    };

    this.projectsService.update(Number(this.projectId), projectData).subscribe({
      next: (response) => {
        this.alertNotificationService.success('Project summary updated successfully');
        this.onSummaryUpdated.emit(formData);
      },
      error: (error) => {
        console.error('Error updating project summary:', error);
        this.alertNotificationService.error('Error updating project summary');
      }
    });
  }

  addInspector(): void {
    const selectedInspectorKey = this.inspectorSelectControl.value;
    if (!selectedInspectorKey) {
      this.alertNotificationService.error('Please select an inspector');
      return;
    }
    
    if (!this.projectId) {
      this.alertNotificationService.error('Invalid project ID');
      return;
    }

    const selectedInspector = this.inspectorOptions.find(option => option.key == selectedInspectorKey);
    console.log(selectedInspectorKey);
    console.log(this.inspectorOptions);
    
    
    if (!selectedInspector) {
      this.alertNotificationService.error('Selected inspector not found');
      return;
    }

    const isAlreadyAssigned = this.inspectors.some(inspector => {
      const inspectorUserId = inspector.user?.userId || inspector.user?.id || inspector.userId || inspector.id;
      return inspectorUserId == selectedInspectorKey;
    });

    if (isAlreadyAssigned) {
      this.alertNotificationService.error('Inspector is already assigned to this project');
      this.inspectorSelectControl.setValue('');
      return;
    }

    this.isLoadingInspectors = true;
    this.inspectorSelectControl.disable();
    
    const inspectorData = {
      userId: selectedInspectorKey,
      accessType: 'inspector' 
    };
    
    this.projectsService.createProjectInspectors(inspectorData, Number(this.projectId)).subscribe({
      next: (response) => {
        this.alertNotificationService.success('Inspector added successfully');
        this.inspectorSelectControl.setValue('');
        this.loadProjectInspectors(); 
        this.loadAvailableInspectors();
      },
      error: (error) => {
        console.error('Error adding inspector:', error);
        const errorMessage = error.error?.message || error.message || 'Error adding inspector';
        this.alertNotificationService.error(errorMessage);
        this.inspectorSelectControl.enable();
      },
      complete: () => {
        this.isLoadingInspectors = false;
      }
    });
  }

  onTableAction(action: { action: string; item: any }): void {
    if (action.action === 'delete') {
      this.confirmDeleteInspector(action.item);
    }
  }

  confirmDeleteInspector(inspector: any): void {
    this.inspectorToDelete = inspector;
    const message = `Are you sure you want to delete the inspector  <strong>${inspector.user.firstName} ${inspector.user.lastName}</strong>?`;
    
    this.confirmationDialogService.open(
      message,
      () => {
        if (this.inspectorToDelete && this.projectId) {
          this.isLoadingInspectors = true;
          const inspectorId = this.inspectorToDelete.projectUserAccessId;     
          
          this.projectsService.deleteProjectInspectors(Number(this.projectId), inspectorId).subscribe({
            next: (response) => {
              this.alertNotificationService.success('Inspector removed successfully');
              this.loadProjectInspectors(); 
              this.loadAvailableInspectors();
            },
            error: (error) => {
              console.error('Error removing inspector:', error);
              this.alertNotificationService.error('Error removing inspector');
            },
            complete: () => {
              this.isLoadingInspectors = false;
              this.inspectorToDelete = null;
            }
          });
        }
      },
      () => { this.inspectorToDelete = null; }
    );
  }

  getInspectorName(inspector: any): string {
    if (inspector.user) {
      return `${inspector.user.firstName || ''} ${inspector.user.lastName || ''}`.trim();
    } else if (inspector.name) {
      return inspector.name;
    } else if (inspector.firstName || inspector.lastName) {
      return `${inspector.firstName || ''} ${inspector.lastName || ''}`.trim();
    }
    return 'Unknown Inspector';
  }

  
  trackByInspector(index: number, inspector: any): string | number {
    return inspector?.projectUserAccessId ?? inspector?.id ?? `inspector_${index}`;
  }

  loadProjectInspectors(): void {
    if (!this.projectId) return;
    
    this.isLoadingInspectors = true;
    const params = new HttpParams().set('size', '9999');
    
    this.projectsService.listProjectInspectors(Number(this.projectId), { params }).subscribe({
      next: (response: any) => {
        if (response.status && response.status.code === 200) {
          this.inspectors = response.data?.projectUserAccesses || [];
        }
      },
      error: (error: any) => {
        console.error('Error loading project inspectors:', error);
        this.alertNotificationService.error('Error loading project inspectors');
      },
      complete: () => {
        this.isLoadingInspectors = false;
      }
    });
  }

  loadAvailableInspectors(): void {
    if (!this.projectId) return;
    
    this.isLoadingAvailableInspectors = true;
    this.inspectorSelectControl.disable();
    
    
    this.projectsService.optionsProjectInspectors(Number(this.projectId)).subscribe({
      next: (response: any) => {
        if (response.status && response.status.code === 200) {
          this.availableInspectors = response.data?.users;
          this.inspectorOptions = this.availableInspectors.map((user:any) => ({
              key: user.userId,
              value: `${user.firstName} ${user.lastName}`.trim() 
            }));
        }
      },
      error: (error: any) => {
        console.error('Error loading available inspectors:', error);
        this.alertNotificationService.error('Error loading available inspectors');
      },
      complete: () => {
        this.isLoadingAvailableInspectors = false;
        this.inspectorSelectControl.enable();
      }
    });
  }
}
